import type { KbLookupStatus } from '../api/types';

/**
 * KB lookup status badge — appears next to artifact rows to show whether
 * the platform's bundled Cryptography KB classified the artifact:
 *
 *   Resolved    — green; KB classified this row, risk_level is trusted
 *   NeedsReview — amber; algorithm matched the alias table but the
 *                 specific (size, curve) combination has no KB row.
 *                 Customer should confirm classification.
 *   Unknown     — red; algorithm name didn't match any alias. Could be
 *                 a typo, a custom protocol, or a tenant addition needed.
 */
const PALETTE: Record<KbLookupStatus, { bg: string; fg: string; label: string }> = {
  Resolved:    { bg: 'bg-risk-no_risk/10',   fg: 'text-risk-no_risk',   label: 'KB resolved' },
  NeedsReview: { bg: 'bg-risk-moderate/10',  fg: 'text-risk-moderate',  label: 'Needs review' },
  Unknown:     { bg: 'bg-risk-critical/10',  fg: 'text-risk-critical',  label: 'KB unknown' },
};

export function KbLookupStatusBadge({ status }: { status: KbLookupStatus }) {
  const p = PALETTE[status];
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 text-[10px] tracking-wide rounded font-mono uppercase ${p.bg} ${p.fg}`}
      title={
        status === 'Resolved' ? 'Bundled KB classified this artifact'
        : status === 'NeedsReview' ? 'Algorithm recognised but specific (size, curve) needs human classification'
        : 'Algorithm name not recognised — add a tenant alias or correct the spelling'
      }
    >
      {p.label}
    </span>
  );
}
